/**
 * Parts Page - Modern UI Design
 * تصميم حديث واحترافي مثل SaaS apps
 */
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Scissors, Plus, Download, Folder, FileText, Users, Settings, Play } from 'lucide-react';
import { PartsManager } from './PartsManager';
import { GroupsManager } from './GroupsManager';
import { PartsExporter } from './PartsExporter';
import { Card } from '../../ui/common/Card';
import { Button } from '../../ui/common/Button';
import { lightTheme } from '../../ui/design/theme';

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: ${props => props.theme.background || '#f8f9fa'};
  overflow: hidden;
`;

const Header = styled.div`
  background: ${props => props.theme.surfaceElevated || '#fff'};
  border-bottom: 1px solid ${props => props.theme.border || '#e0e0e0'};
  padding: ${props => props.theme.spacing?.lg || '24px'};
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: ${props => props.theme.shadow || '0 2px 4px rgba(0,0,0,0.05)'};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing?.md || '16px'};
`;

const HeaderTitle = styled.h1`
  font-size: ${props => props.theme.fontSize?.['2xl'] || '24px'};
  font-weight: 700;
  color: ${props => props.theme.text || '#000'};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing?.sm || '8px'};
`;

const HeaderStats = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing?.lg || '24px'};
  align-items: center;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing?.xs || '4px'};
`;

const StatValue = styled.span`
  font-size: ${props => props.theme.fontSize?.lg || '18px'};
  font-weight: 600;
  color: ${props => props.theme.text || '#000'};
`;

const StatLabel = styled.span`
  font-size: ${props => props.theme.fontSize?.xs || '12px'};
  color: ${props => props.theme.textSecondary || '#666'};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${props => props.theme.spacing?.lg || '24px'};
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: ${props => props.theme.spacing?.lg || '24px'};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const QuickActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing?.sm || '8px'};
  flex-wrap: wrap;
`;

export const PartsPageModern = ({ project, onUpdate, currentTime, duration, onSeek, isPlaying, onPlayPause }) => {
  const [parts, setParts] = useState(project.parts || []);
  const [groups, setGroups] = useState(project.groups || []);

  useEffect(() => {
    setParts(project.parts || []);
    setGroups(project.groups || []);
  }, [project]);

  const handlePartsUpdate = (updatedParts) => {
    setParts(updatedParts);
    onUpdate({ parts: updatedParts });
  };

  const handleGroupsUpdate = (updatedGroups) => {
    setGroups(updatedGroups);
    onUpdate({ groups: updatedGroups });
  };

  const handleExportComplete = ({ part, result }) => {
    console.log('✅ Export completed:', { part: part.name, result });
  };

  return (
    <Container theme={lightTheme}>
      <Header theme={lightTheme}>
        <HeaderLeft>
          <HeaderTitle theme={lightTheme}>
            <Scissors size={28} />
            Parts Cutter
          </HeaderTitle>
        </HeaderLeft>
        <HeaderStats>
          <StatItem>
            <StatValue theme={lightTheme}>{parts.length}</StatValue>
            <StatLabel theme={lightTheme}>Parts</StatLabel>
          </StatItem>
          <StatItem>
            <StatValue theme={lightTheme}>{groups.length}</StatValue>
            <StatLabel theme={lightTheme}>Groups</StatLabel>
          </StatItem>
        </HeaderStats>
      </Header>

      <Content theme={lightTheme}>
        {/* Parts Management Card */}
        <Card
          title="Parts Management"
          icon={<FileText size={20} />}
          elevated
          hoverable
          theme={lightTheme}
        >
          <PartsManager 
            project={{ ...project, parts }}
            onUpdate={handlePartsUpdate}
          />
        </Card>

        {/* Groups Management Card */}
        <Card
          title="Groups Management"
          icon={<Folder size={20} />}
          elevated
          hoverable
          theme={lightTheme}
        >
          <GroupsManager 
            project={{ ...project, groups }}
            onUpdate={handleGroupsUpdate}
          />
        </Card>

        {/* Export Card */}
        <Card
          title="Export"
          icon={<Download size={20} />}
          elevated
          hoverable
          theme={lightTheme}
          fullWidth
        >
          <PartsExporter 
            parts={parts}
            groups={groups}
            project={project}
            onExportComplete={handleExportComplete}
          />
        </Card>
      </Content>
    </Container>
  );
};

